import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardMedia, Grid, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import PageContainer from '../layouts/PageContainer';
import PageState from '../layouts/PageState';
import { getCatalogCategories } from '../services/commerce';

function CategoryTile({ category, onSelect }) {
  const name = category?.name ?? 'Category';
  const img = category?.image || noImage;

  return (
    <Card
      elevation={0}
      onClick={() => onSelect(name)}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:active': { bgcolor: 'action.hover' },
      }}
    >
      <CardMedia
        component="img"
        image={img}
        alt={name}
        sx={{
          width: '100%',
          padding:'10px',
          minHeight: 132,
          objectFit: 'cover',
          bgcolor: 'grey.100',
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = noImage;
        }}
      />
      <Box sx={{ px: 1.25, py: 1.25, flex: 1, display: 'flex', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight={700} textAlign="center" sx={{ width: '100%', lineHeight: 1.3 }}>
          {name}
        </Typography>
      </Box>
    </Card>
  );
}

function CategoriesSkeleton() {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid item xs={6} key={i}>
          <Skeleton variant="rectangular" width="100%" sx={{ aspectRatio: '1', minHeight: 132, borderRadius: 2 }} />
          <Skeleton width="80%" height={24} sx={{ mx: 'auto', mt: 1 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export default function Categories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCatalogCategories();
      setCategories(res.data ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const goToCatalog = (categoryName) => {
    navigate(`/v2/catalog?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <PageContainer title="Categories">
      {loading && <CategoriesSkeleton />}

      {!loading && error && (
        <PageState loading={false} error={error} onRetry={load}>
          {null}
        </PageState>
      )}

      {!loading && !error && categories.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No categories available.
        </Typography>
      )}

      {!loading && !error && categories.length > 0 && (
        <Grid container spacing={2}>
          {categories.map((cat) => (
            <Grid item xs={6} key={cat.documentId || cat.id || cat.name}>
              <CategoryTile category={cat} onSelect={goToCatalog} />
            </Grid>
          ))}
        </Grid>
      )}
    </PageContainer>
  );
}
